import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';

// --- The Autonomous Symbolic Brain ---
class SymbolicEngine {
    // 1. Environmental & Frustration Logic
    static evaluateTheme(memory) {
        const currentHour = new Date().getHours();
        const isLateNight = currentHour >= 22 || currentHour <= 5; 
        
        if (memory.behavior.rageClicks > 3 || isLateNight) return "theme-cinematic-dark";
        if (memory.behavior.readingFocus > 50) return "theme-universal-glass";
        return "theme-cinematic-dark";
    }

    // 2. Dynamic Intent Identification (No hardcoded keys)
    static getDominantInterest(memory) {
        let dominant = "general";
        let maxWeight = 0;
        
        // Dynamically scans whatever symbols exist in the user's unique matrix
        for (const [symbol, weight] of Object.entries(memory.interests || {})) {
            if (weight > maxWeight) {
                maxWeight = weight;
                dominant = symbol;
            }
        }
        return dominant;
    }

    // 3. UI Actuator Rules
    static evaluateUIState(memory, dominant) {
        const isHighlyInterested = (memory.interests[dominant] || 0) > 20;
        return {
            simplifyAnimations: (memory.behavior.rageClicks || 0) > 3,
            highlightCTAs: (memory.behavior.rageClicks || 0) > 5 || isHighlyInterested
        };
    }

    // 4. Content Rearrangement
    static arrangeContent(portfolioData, memory) {
        if (!portfolioData) return [];
        return [...portfolioData].sort((a, b) => {
            const weightA = memory.interests[a.symbol] || 0;
            const weightB = memory.interests[b.symbol] || 0;
            return weightB - weightA; 
        });
    }

    // 5. Advanced Neural Decay
    static applyDecay(memory) {
        const decayRate = 0.95; 
        let decayedInterests = {};
        for (let key in memory.interests) {
            // Prune dead memories (less than 0.5 weight) to save local storage space
            const newWeight = memory.interests[key] * decayRate;
            if (newWeight > 0.5) decayedInterests[key] = newWeight;
        }
        return { ...memory, interests: decayedInterests };
    }
}

// --- Context Setup ---
const KernelContext = createContext();

export const AIKernelProvider = ({ children, portfolioData }) => {
    // --- Memory Initialization ---
    const [memory, setMemory] = useState(() => {
        const savedMemory = localStorage.getItem('agent_memory_matrix');
        const parsed = savedMemory ? JSON.parse(savedMemory) : null;
        
        if (parsed) return SymbolicEngine.applyDecay(parsed);
        
        // Dynamic schema: starts empty, learns as user interacts
        return { interests: {}, behavior: { readingFocus: 0, rageClicks: 0 } };
    });

    const [agentState, setAgentState] = useState({
        theme: "theme-cinematic-dark",
        dominantInterest: "general",
        ui: { simplifyAnimations: false, highlightCTAs: false }
    });
    
    const [arrangedPortfolio, setArrangedPortfolio] = useState(portfolioData);
    
    // --- The Telemetry Queue (Nervous System) ---
    const telemetryQueue = useRef([]);

    // --- The Cognitive Loop ---
    useEffect(() => {
        const dominant = SymbolicEngine.getDominantInterest(memory);
        const newState = {
            theme: SymbolicEngine.evaluateTheme(memory),
            dominantInterest: dominant,
            ui: SymbolicEngine.evaluateUIState(memory, dominant)
        };
        
        setAgentState(newState);

        // Strict Manual Override Check: Only alter DOM theme if user hasn't locked it
        if (!localStorage.getItem('manual_theme')) {
            document.documentElement.setAttribute('data-theme', newState.theme);
        }

        if (portfolioData && portfolioData.length > 0) {
            setArrangedPortfolio(SymbolicEngine.arrangeContent(portfolioData, memory));
        }

        localStorage.setItem('agent_memory_matrix', JSON.stringify(memory));

        // Push current state to the background queue instead of firing instantly
        telemetryQueue.current.push({ memory, dominant, timestamp: Date.now() });

    }, [memory, portfolioData]);

    // --- Background Telemetry Transmitter ---
    // Runs once on mount, sets up a background interval to flush the queue
    useEffect(() => {
        const flushInterval = setInterval(() => {
            if (telemetryQueue.current.length > 0) {
                // Take the most recent snapshot from the queue
                const latestPayload = telemetryQueue.current[telemetryQueue.current.length - 1];
                sendTelemetryToBackend(latestPayload.memory, latestPayload.dominant);
                
                // Clear the queue
                telemetryQueue.current = [];
            }
        }, 15000); // Flush every 15 seconds

        return () => clearInterval(flushInterval);
    }, []);

    // --- High-Performance Sensor Interface ---
    // useCallback prevents React from rebuilding this function on every render
    const reportBehavior = useCallback((symbol, weight, type = 'interest') => {
        setMemory(prev => {
            const newState = { ...prev };
            
            // Dynamically instantiate new memory nodes if they don't exist
            if (type === 'interest') {
                if (!newState.interests[symbol]) newState.interests[symbol] = 0;
                newState.interests[symbol] += weight;
            } else if (type === 'behavior') {
                if (!newState.behavior[symbol]) newState.behavior[symbol] = 0;
                newState.behavior[symbol] += weight;
            }
            return newState;
        });
    }, []);

    // --- Memoized Context Value ---
    // Prevents the entire application from re-rendering unless the actual data changes
    const contextValue = useMemo(() => ({
        memory,
        agentState,
        arrangedPortfolio,
        reportBehavior
    }), [memory, agentState, arrangedPortfolio, reportBehavior]);

    return (
        <KernelContext.Provider value={contextValue}>
            <div className={`kernel-wrapper ${agentState.theme}`}>
                {children}
            </div>
        </KernelContext.Provider>
    );
};

export const useKernel = () => useContext(KernelContext);

// --- Robust Backend Transmission Protocol ---
const sendTelemetryToBackend = async (memory, dominant) => {
    try {
        // Use keepalive to ensure the request finishes even if the user closes the tab
        await fetch('https://apiv1.harishacrypto.me/service4/api/telemetry', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ memory, dominant }),
            keepalive: true 
        });
        console.log("📡 [Agent] Telemetry Queue Flushed.");
    } catch (error) {
        console.warn("Telemetry transmission blocked or failed - edge mode sustained.");
    }
};